// src/redux/slices/userSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import API_ROUTES from '../../config/apiRoutes';

// 1) Fetch the full users list
export const fetchUsers = createAsyncThunk(
  'users/fetchUsers',
  async (filterObj = {}, { getState, rejectWithValue }) => {
    const { token } = getState().auth;
    const requestBody = {
      limit: filterObj.limit ?? 100,
      currentPage: filterObj.currentPage ?? 1,
      byKeyword: filterObj.byKeyword ?? "",
    };
    try {
      const response = await axios.post(
        API_ROUTES.Users.GET_ALL,
        requestBody,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // response.data.data is your array
      return response.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

// NEW: Fetch identity verification data
export const fetchIdentityVerification = createAsyncThunk(
  'users/fetchIdentityVerification',
  async (_, { getState, rejectWithValue }) => {
    const { token } = getState().auth;
    try {
      const endpoint = API_ROUTES.Users.IDENTITY_VERIFICATION_FILTER;
      console.log('API_ROUTES object:', API_ROUTES.Users);
      console.log('Fetching identity verification data from:', endpoint);
      
      // Fallback to hardcoded URL if the route is undefined
      const finalEndpoint = endpoint || 'http://43.204.150.34:3000/api/v2/admins/users/identity-verification/filter';
      console.log('Final endpoint used:', finalEndpoint);
      
      const response = await axios.post(
        finalEndpoint,
        {}, // Empty payload as requested
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          } 
        }
      );
      console.log('Identity verification response:', response.data);
      return response.data;
    } catch (err) {
      console.error('Error fetching identity verification:', err);
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

// 2) Fetch a single user's details
export const fetchUserDetails = createAsyncThunk(
  'users/fetchUserDetails',
  async (userId, { getState, rejectWithValue }) => {
    const { token } = getState().auth;
    try {
      const response = await axios.get(
        API_ROUTES.Users.GET_USER_DETAIL(userId),
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

// 3) Fetch clips with pagination
export const fetchUserClips = createAsyncThunk(
  'users/fetchUserClips',
  async ({ userId, currentPage = 1, limit = 10 }, { getState, rejectWithValue }) => {
    const { token } = getState().auth;
    try {
      const response = await axios.post(
        API_ROUTES.CLIPS.GET_ALL,
        { 
          byUserId: userId,
          currentPage,
          limit
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Extract the pagination data from the response
      const responseData = response.data.data || response.data;
      const clipsData = responseData.clips || responseData.data || responseData;
      
      return {
        clips: Array.isArray(clipsData) ? clipsData : [],
        total: responseData.total || 0,
        totalPages: responseData.totalPages || 1,
        currentPage: currentPage
      };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

// 4b) Fetch channels created by a specific user with pagination
export const fetchUserChannels = createAsyncThunk(
  'users/fetchUserChannels',
  async ({ userId, currentPage = 1, limit = 10 }, { getState, rejectWithValue }) => {
    const { token } = getState().auth;
    try {
      const response = await axios.post(
        API_ROUTES.CHANNELS.GET_ALL,
        { byUserId: userId, currentPage, limit },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Channels API returns { channels, totalCount, currentPage, totalPages }
      const payload = response.data || {};
      const arr = payload.channels || payload.data || [];
      return {
        channels: Array.isArray(arr) ? arr : [],
        total: payload.totalCount ?? payload.total ?? arr.length,
        totalPages: payload.totalPages ?? 1,
        currentPage,
      };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

// 4a) Fetch orders for a specific user with pagination
export const fetchUserOrders = createAsyncThunk(
  'users/fetchUserOrders',
  async ({ userId, currentPage = 1, limit = 10 }, { getState, rejectWithValue }) => {
    const { token } = getState().auth;
    try {
      const response = await axios.post(
        API_ROUTES.Orders.GET_ALL,
        {
          byUserId: userId,
          currentPage,
          limit,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const payload = response.data || {};
      const ordersArr = payload.data || payload.orders || [];
      return {
        orders: Array.isArray(ordersArr) ? ordersArr : [],
        total: payload.total ?? ordersArr.length,
        totalPages: payload.totalPages ?? 1,
        currentPage,
      };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

// 4) Fetch auctions with pagination
export const fetchUserAuctions = createAsyncThunk(
  'users/fetchUserAuctions',
  async ({ userId, currentPage = 1, limit = 10 }, { getState, rejectWithValue }) => {
    const { token } = getState().auth;
    try {
      const response = await axios.post(
        API_ROUTES.Users.GET_AUCTIONS,
        { 
          byUserId: userId,
          currentPage,
          limit
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // The API returns { data: { auctions: [...], total: number, totalPages: number } }
      const responseData = response.data.data || response.data;
      const auctionsArray = responseData.auctions || responseData.data || responseData;
      
      return {
        auctions: Array.isArray(auctionsArray) ? auctionsArray : [],
        total: responseData.total || 0,
        totalPages: responseData.totalPages || 1,
        currentPage: currentPage
      };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

// NEW: toggle auction status (live ↔ blocked)
export const updateUserAuctionStatus = createAsyncThunk(
  'users/updateUserAuctionStatus',
  async ({ auctionId, status }, { getState, rejectWithValue }) => {
    const { token } = getState().auth;
    try {
      await axios.patch(
        API_ROUTES.Users.UPDATE_AUCTION_STATUS(auctionId),
        { status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // return the change so we can update local store
      return { auctionId, status };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to update auction status'
      );
    }
  }
);

// NEW: Update clip status
export const updateUserClipStatus = createAsyncThunk(
  'users/updateUserClipStatus',
  async ({ clipId, status }, { getState, rejectWithValue }) => {
    const { token } = getState().auth;
    try {
      await axios.patch(
        API_ROUTES.CLIPS.UPDATE_STATUS(clipId),
        { status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return { clipId, status };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to update clip status'
      );
    }
  }
);

// NEW: Update verification status
export const updateVerificationStatus = createAsyncThunk(
  'users/updateVerificationStatus',
  async ({ userId, status, remarks }, { getState, rejectWithValue }) => {
    const { token } = getState().auth;
    try {
      const response = await axios.patch(
        API_ROUTES.Users.UPDATE_VERIFICATION_STATUS,
        {
          userId,
          status,
          remarks
        },
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          } 
        }
      );
      return { userId, status, remarks };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

// NEW: Sync with MyFatorah
export const syncWithMyFatorah = createAsyncThunk(
  'users/syncWithMyFatorah',
  async (userId, { getState, rejectWithValue }) => {
    const { token } = getState().auth;
    console.log('syncWithMyFatorah action called with userId:', userId);
    console.log('API_ROUTES object:', API_ROUTES);
    console.log('API_ROUTES.Users:', API_ROUTES.Users);
    console.log('SYNC_WITH_MYFATORAH function:', API_ROUTES.Users.SYNC_WITH_MYFATORAH);
    console.log('typeof SYNC_WITH_MYFATORAH:', typeof API_ROUTES.Users.SYNC_WITH_MYFATORAH);
    
    try {
      const endpoint = API_ROUTES.Users.SYNC_WITH_MYFATORAH(userId);
      console.log('Generated endpoint:', endpoint);
      
      const response = await axios.post(
        endpoint,
        {},
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          } 
        }
      );
      return response.data;
    } catch (err) {
      console.error('Error in syncWithMyFatorah action:', err);
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

// NEW: Check MyFatorah Status
export const checkMyFatorahStatus = createAsyncThunk(
  'users/checkMyFatorahStatus',
  async (supplierCode, { getState, rejectWithValue }) => {
    const { token } = getState().auth;
    console.log('checkMyFatorahStatus action called with supplierCode:', supplierCode);
    
    try {
      const endpoint = API_ROUTES.Users.CHECK_MYFATORAH_STATUS(supplierCode);
      console.log('Generated endpoint:', endpoint);
      
      const response = await axios.post(
        endpoint,
        {},
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          } 
        }
      );
      return response.data;
    } catch (err) {
      console.error('Error in checkMyFatorahStatus action:', err);
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

const usersSlice = createSlice({
  name: 'users',
  initialState: {
    // **List** state for <UsersPage />
    users: [],
    loading: false,
    error: null,

    // individual detail state
    userDetails: null,
    loadingDetails: false,
    errorDetails: null,

    // clips with pagination
    clips: [],
    clipsTotal: 0,
    clipsTotalPages: 1,
    clipsCurrentPage: 1,
    loadingClips: false,
    errorClips: null,

    // auctions with pagination
    auctions: [],
    auctionsTotal: 0,
    auctionsTotalPages: 1,
    auctionsCurrentPage: 1,
    loadingAuctions: false,
    errorAuctions: null,

    // orders with pagination (per-user view)
    userOrders: [],
    ordersTotal: 0,
    ordersTotalPages: 1,
    ordersCurrentPage: 1,
    loadingOrders: false,
    errorOrders: null,

    // channels with pagination (per-user view)
    userChannels: [],
    channelsTotal: 0,
    channelsTotalPages: 1,
    channelsCurrentPage: 1,
    loadingChannels: false,
    errorChannels: null,

    // verification data state
    loadingVerification: false,
    errorVerification: null,

    // MyFatorah sync state
    syncingMyFatorah: false,
    errorMyFatorah: null,
  },
  reducers: {
    clearUserDetails(state) {
      state.userDetails = null;
      state.clips = [];
      state.clipsTotal = 0;
      state.clipsTotalPages = 1;
      state.clipsCurrentPage = 1;
      state.auctions = [];
      state.auctionsTotal = 0;
      state.auctionsTotalPages = 1;
      state.auctionsCurrentPage = 1;
      state.userOrders = [];
      state.ordersTotal = 0;
      state.ordersTotalPages = 1;
      state.ordersCurrentPage = 1;
      state.userChannels = [];
      state.channelsTotal = 0;
      state.channelsTotalPages = 1;
      state.channelsCurrentPage = 1;
      state.errorDetails = state.errorClips = state.errorAuctions = state.errorOrders = state.errorChannels = null;
    },
    setUserDetails(state, action) {
      state.userDetails = action.payload;
      state.loadingDetails = false;
      state.errorDetails = null;
    },
    updateUsersWithVerification(state, action) {
      const verificationData = action.payload;
      if (verificationData && Array.isArray(verificationData)) {
        // Create a map of verification data by user ID
        const verificationMap = new Map();
        verificationData.forEach(user => {
          if (user.identityVerification) {
            verificationMap.set(user._id, user);
          }
        });

        // Update the users array with verification information
        state.users = state.users.map(user => {
          const verificationUser = verificationMap.get(user._id);
          if (verificationUser) {
            return {
              ...user,
              identityVerification: verificationUser.identityVerification,
              isVerified: verificationUser.isVerified || false
            };
          }
          return user;
        });
      }
    },
  },
  extraReducers: (builder) => {
    // —— fetchUsers (list) ——
    builder
      .addCase(fetchUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.users = action.payload;
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // —— fetchIdentityVerification ——
    builder
      .addCase(fetchIdentityVerification.pending, (state) => {
        state.loadingVerification = true;
        state.errorVerification = null;
      })
      .addCase(fetchIdentityVerification.fulfilled, (state, action) => {
        state.loadingVerification = false;
        // The verification API returns complete user objects with verification data
        const verificationData = action.payload.data;
        console.log('Processing verification data:', verificationData);
        
        if (verificationData && Array.isArray(verificationData)) {
          // Directly use the verification data as the users list since it contains complete user objects
          // Filter to only show users with valid verification types
          state.users = verificationData.filter(user => {
            return user.identityVerification && 
                   user.identityVerification.type && 
                   (user.identityVerification.type === 'personal' || user.identityVerification.type === 'company');
          });
          console.log('Filtered users with verification:', state.users);
        }
      })
      .addCase(fetchIdentityVerification.rejected, (state, action) => {
        state.loadingVerification = false;
        state.errorVerification = action.payload;
      });

    // —— fetchUserDetails ——
    builder
      .addCase(fetchUserDetails.pending, (state) => {
        state.loadingDetails = true;
        state.errorDetails = null;
      })
      .addCase(fetchUserDetails.fulfilled, (state, action) => {
        state.loadingDetails = false;
        const fetchedUserDetails = action.payload;
        
        // Check if we already have verification data for this user in the users list
        const existingUser = state.users.find(user => user._id === fetchedUserDetails._id);
        
        if (existingUser && existingUser.identityVerification) {
          // Merge the fetched details with existing verification data
          state.userDetails = {
            ...fetchedUserDetails,
            identityVerification: existingUser.identityVerification,
            isVerified: existingUser.isVerified
          };
        } else {
          state.userDetails = fetchedUserDetails;
        }
      })
      .addCase(fetchUserDetails.rejected, (state, action) => {
        state.loadingDetails = false;
        state.errorDetails = action.payload;
      });

    // —— fetchUserClips ——
    builder
      .addCase(fetchUserClips.pending, (state) => {
        state.loadingClips = true;
        state.errorClips = null;
      })
      .addCase(fetchUserClips.fulfilled, (state, action) => {
        state.loadingClips = false;
        state.clips = action.payload.clips;
        state.clipsTotal = action.payload.total;
        state.clipsTotalPages = action.payload.totalPages;
        state.clipsCurrentPage = action.payload.currentPage;
      })
      .addCase(fetchUserClips.rejected, (state, action) => {
        state.loadingClips = false;
        state.errorClips = action.payload;
      });

    // —— fetchUserChannels ——
    builder
      .addCase(fetchUserChannels.pending, (state) => {
        state.loadingChannels = true;
        state.errorChannels = null;
      })
      .addCase(fetchUserChannels.fulfilled, (state, action) => {
        state.loadingChannels = false;
        state.userChannels = action.payload.channels;
        state.channelsTotal = action.payload.total;
        state.channelsTotalPages = action.payload.totalPages;
        state.channelsCurrentPage = action.payload.currentPage;
      })
      .addCase(fetchUserChannels.rejected, (state, action) => {
        state.loadingChannels = false;
        state.errorChannels = action.payload;
      });

    // —— fetchUserOrders ——
    builder
      .addCase(fetchUserOrders.pending, (state) => {
        state.loadingOrders = true;
        state.errorOrders = null;
      })
      .addCase(fetchUserOrders.fulfilled, (state, action) => {
        state.loadingOrders = false;
        state.userOrders = action.payload.orders;
        state.ordersTotal = action.payload.total;
        state.ordersTotalPages = action.payload.totalPages;
        state.ordersCurrentPage = action.payload.currentPage;
      })
      .addCase(fetchUserOrders.rejected, (state, action) => {
        state.loadingOrders = false;
        state.errorOrders = action.payload;
      });

    // —— fetchUserAuctions ——
    builder
      .addCase(fetchUserAuctions.pending, (state) => {
        state.loadingAuctions = true;
        state.errorAuctions = null;
      })
      .addCase(fetchUserAuctions.fulfilled, (state, action) => {
        state.loadingAuctions = false;
        state.auctions = action.payload.auctions;
        state.auctionsTotal = action.payload.total;
        state.auctionsTotalPages = action.payload.totalPages;
        state.auctionsCurrentPage = action.payload.currentPage;
      })
      .addCase(fetchUserAuctions.rejected, (state, action) => {
        state.loadingAuctions = false;
        state.errorAuctions = action.payload;
      });
      
    builder
      // Handle our new action:
      .addCase(updateUserAuctionStatus.fulfilled, (state, action) => {
        const { auctionId, status } = action.payload;
        state.auctions = state.auctions.auctions ? 
          { ...state.auctions, auctions: state.auctions.auctions.map((a) =>
            a._id === auctionId ? { ...a, status } : a
          )} :
          state.auctions.map((a) =>
            a._id === auctionId ? { ...a, status } : a
          );
      })
      .addCase(updateUserClipStatus.fulfilled, (state, action) => {
        const { clipId, status } = action.payload;
        state.clips = state.clips.map((c) =>
          c._id === clipId ? { ...c, status } : c
        );
      })
      .addCase(updateVerificationStatus.fulfilled, (state, action) => {
        const { userId, status, remarks } = action.payload;
        // Find the user and update their verification status
        const userToUpdate = state.users.find(user => user._id === userId);
        if (userToUpdate) {
          userToUpdate.identityVerification.status = status;
          userToUpdate.identityVerification.remarks = remarks;
        }
      })
      .addCase(syncWithMyFatorah.pending, (state) => {
        state.syncingMyFatorah = true;
        state.errorMyFatorah = null;
      })
      .addCase(syncWithMyFatorah.fulfilled, (state, action) => {
        state.syncingMyFatorah = false;
        // Optionally update user data if needed
      })
      .addCase(syncWithMyFatorah.rejected, (state, action) => {
        state.syncingMyFatorah = false;
        state.errorMyFatorah = action.payload;
      })
      .addCase(checkMyFatorahStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(checkMyFatorahStatus.fulfilled, (state, action) => {
        state.loading = false;
        // Optionally handle the response data
      })
      .addCase(checkMyFatorahStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearUserDetails, setUserDetails, updateUsersWithVerification } = usersSlice.actions;
export default usersSlice.reducer;
